import React, { useState, useEffect } from 'react';
import {
  createEmployee,
  createEmployeeEntry,
  createEmployeePayment,
  deleteEmployeeEntry,
  deleteEmployeePayment,
  fetchPayBalances,
  listEmployeeEntries,
  listEmployeePayments,
  listEmployees,
  resetEmployeePin,
  updateEmployeeEntry,
  updateEmployee,
  type EmployeeBalanceItem,
  type EmployeePayment,
  type EmployeeTimeEntry,
  type EmployeeSummary,
} from '../api/timeTracking';
import { borderRadius, colors, spacing, typographyFlat } from '../styles/tokens';
import { formatInstant, parseInstant } from '../utils/datetime';

type EntryDraft = {
  clockIn: string;
  clockOut: string;
  note: string;
};

type PaymentDraft = {
  amount: string;
  paidAt: string;
  note: string;
};

type HistoryFilter = {
  startDate: string;
  endDate: string;
};

type PresetRange = {
  startDate: string;
  endDate: string;
};

const formatDisplayDate = (iso?: string | null) => formatInstant(iso, '-');

const toInputDateTime = (iso?: string | null) => {
  if (!iso) return '';
  const date = parseInstant(iso);
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const toIsoFromInput = (value: string) => {
  if (!value) return undefined;
  return new Date(value).toISOString();
};

const entryMatchesDateRange = (entry: EmployeeTimeEntry, filter: HistoryFilter | undefined) => {
  if (!filter) return true;

  const entryDate = parseInstant(entry.clock_in_at);
  if (!entryDate) return false;

  if (filter.startDate) {
    const start = new Date(`${filter.startDate}T00:00:00`);
    if (entryDate < start) return false;
  }

  if (filter.endDate) {
    const end = new Date(`${filter.endDate}T23:59:59.999`);
    if (entryDate > end) return false;
  }

  return true;
};

const toDateOnly = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getThisWeekRange = (): PresetRange => {
  const now = new Date();
  const day = now.getDay();
  const mondayOffset = day === 0 ? -6 : 1 - day;
  const start = new Date(now);
  start.setDate(now.getDate() + mondayOffset);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  return { startDate: toDateOnly(start), endDate: toDateOnly(end) };
};

const getLastWeekRange = (): PresetRange => {
  const thisWeek = getThisWeekRange();
  const start = new Date(`${thisWeek.startDate}T00:00:00`);
  start.setDate(start.getDate() - 7);
  const end = new Date(`${thisWeek.endDate}T00:00:00`);
  end.setDate(end.getDate() - 7);
  return { startDate: toDateOnly(start), endDate: toDateOnly(end) };
};

const getCurrentPayPeriodRange = (): PresetRange => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const day = now.getDate();

  const start = new Date(year, month, day <= 15 ? 1 : 16);
  const end = day <= 15
    ? new Date(year, month, 15)
    : new Date(year, month + 1, 0);

  return { startDate: toDateOnly(start), endDate: toDateOnly(end) };
};

const formatActiveRangeLabel = (filter: HistoryFilter | undefined) => {
  if (!filter || (!filter.startDate && !filter.endDate)) {
    return 'Showing all entry dates';
  }

  if (filter.startDate && filter.endDate) {
    return `Showing entries from ${filter.startDate} to ${filter.endDate}`;
  }

  if (filter.startDate) {
    return `Showing entries on or after ${filter.startDate}`;
  }

  return `Showing entries on or before ${filter.endDate}`;
};

const formatMoney = (value: number | string | null | undefined) => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  const amount = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(amount)) {
    return '-';
  }
  return `$${amount.toFixed(2)}`;
};

const toRateInput = (value: number | string | null | undefined) => {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  return String(value);
};

const emptyPaymentDraft = (): PaymentDraft => ({
  amount: '',
  paidAt: toInputDateTime(new Date().toISOString()),
  note: '',
});

export const EmployeeManagementCard: React.FC = () => {
  // Form state
  const [newEmployeeCode, setNewEmployeeCode] = useState('');
  const [newEmployeeName, setNewEmployeeName] = useState('');
  const [newEmployeePin, setNewEmployeePin] = useState('');
  const [newEmployeeTimezone, setNewEmployeeTimezone] = useState('America/Los_Angeles');
  const [newEmployeeHourlyRate, setNewEmployeeHourlyRate] = useState('');

  // Employee list state
  const [employees, setEmployees] = useState<EmployeeSummary[]>([]);
  const [balancesByEmployee, setBalancesByEmployee] = useState<Record<string, EmployeeBalanceItem>>({});
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [employeesError, setEmployeesError] = useState<string | null>(null);

  // PIN reset state
  const [resetPins, setResetPins] = useState<Record<string, string>>({});
  const [rateDrafts, setRateDrafts] = useState<Record<string, string>>({});
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(null);
  const [historyByEmployee, setHistoryByEmployee] = useState<Record<string, EmployeeTimeEntry[]>>({});
  const [paymentsByEmployee, setPaymentsByEmployee] = useState<Record<string, EmployeePayment[]>>({});
  const [historyLoadingByEmployee, setHistoryLoadingByEmployee] = useState<Record<string, boolean>>({});
  const [entryDraftByEmployee, setEntryDraftByEmployee] = useState<Record<string, EntryDraft>>({});
  const [paymentDraftByEmployee, setPaymentDraftByEmployee] = useState<Record<string, PaymentDraft>>({});
  const [editDraftByEntry, setEditDraftByEntry] = useState<Record<string, EntryDraft>>({});
  const [historyFilterByEmployee, setHistoryFilterByEmployee] = useState<Record<string, HistoryFilter>>({});

  const reloadEmployeesAndBalances = async () => {
    const [employeeData, balanceData] = await Promise.all([listEmployees(), fetchPayBalances()]);
    setEmployees(employeeData);
    setBalancesByEmployee(
      Object.fromEntries(balanceData.employees.map(item => [item.employee_id, item])),
    );
    setRateDrafts(prev => {
      const next = { ...prev };
      for (const employee of employeeData) {
        if (next[employee.id] === undefined) {
          next[employee.id] = toRateInput(employee.hourly_rate);
        }
      }
      return next;
    });
  };

  // Load employees on mount
  useEffect(() => {
    const loadEmployees = async () => {
      try {
        setEmployeesLoading(true);
        setEmployeesError(null);
        await reloadEmployeesAndBalances();
      } catch (err) {
        setEmployeesError(err instanceof Error ? err.message : 'Failed to load employees');
      } finally {
        setEmployeesLoading(false);
      }
    };

    void loadEmployees();
  }, []);

  const handleCreateEmployee = async () => {
    try {
      setEmployeesError(null);
      const rateValue = newEmployeeHourlyRate.trim();
      await createEmployee({
        employee_code: newEmployeeCode.trim(),
        display_name: newEmployeeName.trim(),
        pin: newEmployeePin,
        timezone: newEmployeeTimezone.trim(),
        hourly_rate: rateValue ? Number(rateValue) : null,
      });
      setNewEmployeeCode('');
      setNewEmployeeName('');
      setNewEmployeePin('');
      setNewEmployeeTimezone('America/Los_Angeles');
      setNewEmployeeHourlyRate('');

      await reloadEmployeesAndBalances();
    } catch (err) {
      setEmployeesError(err instanceof Error ? err.message : 'Failed to create employee');
    }
  };

  const handleResetPin = async (employeeId: string) => {
    const newPin = resetPins[employeeId];
    if (!newPin) {
      setEmployeesError('Enter a new PIN');
      return;
    }
    try {
      setEmployeesError(null);
      await resetEmployeePin(employeeId, newPin);
      setResetPins({ ...resetPins, [employeeId]: '' });

      await reloadEmployeesAndBalances();
    } catch (err) {
      setEmployeesError(err instanceof Error ? err.message : 'Failed to reset PIN');
    }
  };

  const handleSaveHourlyRate = async (employeeId: string) => {
    const draft = (rateDrafts[employeeId] ?? '').trim();
    try {
      setEmployeesError(null);
      await updateEmployee(employeeId, {
        hourly_rate: draft === '' ? null : Number(draft),
      });
      await reloadEmployeesAndBalances();
    } catch (err) {
      setEmployeesError(err instanceof Error ? err.message : 'Failed to update hourly rate');
    }
  };

  const handleToggleEmployeeActive = async (employee: EmployeeSummary) => {
    try {
      setEmployeesError(null);
      await updateEmployee(employee.id, {
        is_active: !employee.is_active,
      });

      await reloadEmployeesAndBalances();
    } catch (err) {
      setEmployeesError(err instanceof Error ? err.message : 'Failed to update employee');
    }
  };

  const loadEmployeeHistory = async (employeeId: string) => {
    try {
      setEmployeesError(null);
      setHistoryLoadingByEmployee(prev => ({ ...prev, [employeeId]: true }));
      const [entries, payments] = await Promise.all([
        listEmployeeEntries(employeeId),
        listEmployeePayments(employeeId),
      ]);
      setHistoryByEmployee(prev => ({ ...prev, [employeeId]: entries }));
      setPaymentsByEmployee(prev => ({ ...prev, [employeeId]: payments }));

      setEditDraftByEntry(prev => {
        const next = { ...prev };
        for (const entry of entries) {
          next[entry.id] = {
            clockIn: toInputDateTime(entry.clock_in_at),
            clockOut: toInputDateTime(entry.clock_out_at ?? undefined),
            note: entry.note ?? '',
          };
        }
        return next;
      });

      if (!entryDraftByEmployee[employeeId]) {
        setEntryDraftByEmployee(prev => ({
          ...prev,
          [employeeId]: {
            clockIn: toInputDateTime(new Date().toISOString()),
            clockOut: '',
            note: '',
          },
        }));
      }

      if (!paymentDraftByEmployee[employeeId]) {
        setPaymentDraftByEmployee(prev => ({
          ...prev,
          [employeeId]: emptyPaymentDraft(),
        }));
      }

      if (!historyFilterByEmployee[employeeId]) {
        setHistoryFilterByEmployee(prev => ({
          ...prev,
          [employeeId]: {
            startDate: '',
            endDate: '',
          },
        }));
      }
    } catch (err) {
      setEmployeesError(err instanceof Error ? err.message : 'Failed to load employee history');
    } finally {
      setHistoryLoadingByEmployee(prev => ({ ...prev, [employeeId]: false }));
    }
  };

  const toggleHistory = async (employeeId: string) => {
    if (selectedEmployeeId === employeeId) {
      setSelectedEmployeeId(null);
      return;
    }
    setSelectedEmployeeId(employeeId);
    await loadEmployeeHistory(employeeId);
  };

  const handleAddEntry = async (employeeId: string) => {
    const draft = entryDraftByEmployee[employeeId];
    if (!draft?.clockIn) {
      setEmployeesError('Clock-in time is required to add an entry');
      return;
    }

    try {
      setEmployeesError(null);
      await createEmployeeEntry(employeeId, {
        clock_in_at: toIsoFromInput(draft.clockIn)!,
        clock_out_at: toIsoFromInput(draft.clockOut),
        note: draft.note.trim() || undefined,
      });
      await loadEmployeeHistory(employeeId);
      await reloadEmployeesAndBalances();
    } catch (err) {
      setEmployeesError(err instanceof Error ? err.message : 'Failed to add entry');
    }
  };

  const handleSaveEntry = async (employeeId: string, entryId: string) => {
    const draft = editDraftByEntry[entryId];
    if (!draft?.clockIn) {
      setEmployeesError('Clock-in time is required when editing an entry');
      return;
    }

    try {
      setEmployeesError(null);
      await updateEmployeeEntry(employeeId, entryId, {
        clock_in_at: toIsoFromInput(draft.clockIn),
        clock_out_at: draft.clockOut ? toIsoFromInput(draft.clockOut) : null,
        note: draft.note.trim() ? draft.note.trim() : null,
      });
      await loadEmployeeHistory(employeeId);
      await reloadEmployeesAndBalances();
    } catch (err) {
      setEmployeesError(err instanceof Error ? err.message : 'Failed to save entry');
    }
  };

  const handleDeleteEntry = async (employeeId: string, entryId: string) => {
    try {
      setEmployeesError(null);
      await deleteEmployeeEntry(employeeId, entryId);
      await loadEmployeeHistory(employeeId);
      await reloadEmployeesAndBalances();
    } catch (err) {
      setEmployeesError(err instanceof Error ? err.message : 'Failed to delete entry');
    }
  };

  const handleAddPayment = async (employeeId: string) => {
    const draft = paymentDraftByEmployee[employeeId] ?? emptyPaymentDraft();
    const amount = Number(draft.amount);
    if (!draft.amount.trim() || Number.isNaN(amount) || amount <= 0) {
      setEmployeesError('Payment amount must be greater than zero');
      return;
    }

    try {
      setEmployeesError(null);
      await createEmployeePayment(employeeId, {
        amount,
        paid_at: toIsoFromInput(draft.paidAt),
        note: draft.note.trim() || undefined,
      });
      setPaymentDraftByEmployee(prev => ({
        ...prev,
        [employeeId]: emptyPaymentDraft(),
      }));
      await loadEmployeeHistory(employeeId);
      await reloadEmployeesAndBalances();
    } catch (err) {
      setEmployeesError(err instanceof Error ? err.message : 'Failed to log payment');
    }
  };

  const handleDeletePayment = async (employeeId: string, paymentId: string) => {
    try {
      setEmployeesError(null);
      await deleteEmployeePayment(employeeId, paymentId);
      await loadEmployeeHistory(employeeId);
      await reloadEmployeesAndBalances();
    } catch (err) {
      setEmployeesError(err instanceof Error ? err.message : 'Failed to delete payment');
    }
  };

  const applyFilterPreset = (employeeId: string, preset: 'thisWeek' | 'lastWeek' | 'thisPayPeriod') => {
    const range = preset === 'thisWeek'
      ? getThisWeekRange()
      : preset === 'lastWeek'
        ? getLastWeekRange()
        : getCurrentPayPeriodRange();

    setHistoryFilterByEmployee(prev => ({
      ...prev,
      [employeeId]: range,
    }));
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Employee Management</h2>
      <p style={styles.subtitle}>
        Create employees, manage PIN access, set hourly rates, and log payments against hours worked.
      </p>

      <div style={styles.card}>
        <h3 style={styles.sectionTitle}>Add New Employee</h3>
        <div style={styles.form}>
          <label style={styles.label}>
            Employee Code
            <input
              style={styles.input}
              value={newEmployeeCode}
              onChange={e => setNewEmployeeCode(e.target.value)}
              placeholder="E100"
              autoComplete="off"
              inputMode="text"
              data-lpignore="true"
              data-form-type="other"
              data-1p-ignore="true"
            />
          </label>
          <label style={styles.label}>
            Display Name
            <input
              style={styles.input}
              value={newEmployeeName}
              onChange={e => setNewEmployeeName(e.target.value)}
              placeholder="Jane Employee"
              autoComplete="off"
              inputMode="text"
              data-lpignore="true"
              data-form-type="other"
              data-1p-ignore="true"
            />
          </label>
          <label style={styles.label}>
            Initial PIN
            <input
              type="text"
              style={styles.input}
              value={newEmployeePin}
              onChange={e => setNewEmployeePin(e.target.value)}
              placeholder="4+ digits"
              autoComplete="off"
              inputMode="numeric"
              data-lpignore="true"
              data-form-type="other"
              data-1p-ignore="true"
            />
          </label>
          <label style={styles.label}>
            Timezone
            <input
              style={styles.input}
              value={newEmployeeTimezone}
              onChange={e => setNewEmployeeTimezone(e.target.value)}
              placeholder="America/Los_Angeles"
              autoComplete="off"
              inputMode="text"
              data-lpignore="true"
              data-form-type="other"
              data-1p-ignore="true"
            />
          </label>
          <label style={styles.label}>
            Hourly Rate (optional)
            <input
              type="number"
              min="0"
              step="0.01"
              style={styles.input}
              value={newEmployeeHourlyRate}
              onChange={e => setNewEmployeeHourlyRate(e.target.value)}
              placeholder="15.00"
            />
          </label>
          <button
            type="button"
            style={styles.button}
            disabled={employeesLoading}
            onClick={() => { void handleCreateEmployee(); }}
          >
            {employeesLoading ? 'Adding...' : 'Add Employee'}
          </button>
        </div>
      </div>

      {employeesError && <div style={styles.error}>{employeesError}</div>}

      <div style={styles.card}>
        <h3 style={styles.sectionTitle}>Employee Directory</h3>
        {employees.length === 0 ? (
          <p style={styles.emptyMessage}>No employees yet. Add one above.</p>
        ) : (
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Name</th>
                  <th style={styles.th}>Code</th>
                  <th style={styles.th}>Rate</th>
                  <th style={styles.th}>Owed</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Reset PIN</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {employees.map((employee: EmployeeSummary) => {
                  const balance = balancesByEmployee[employee.id];
                  return (
                  <React.Fragment key={employee.id}>
                    <tr>
                      <td style={styles.td}>{employee.display_name}</td>
                      <td style={styles.td}>{employee.employee_code}</td>
                      <td style={styles.td}>
                        <div style={styles.rateEditRow}>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            style={styles.rateInput}
                            value={rateDrafts[employee.id] ?? toRateInput(employee.hourly_rate)}
                            onChange={e =>
                              setRateDrafts(prev => ({
                                ...prev,
                                [employee.id]: e.target.value,
                              }))
                            }
                            placeholder="0.00"
                          />
                          <button
                            type="button"
                            style={styles.smallButton}
                            onClick={() => {
                              void handleSaveHourlyRate(employee.id);
                            }}
                          >
                            Save
                          </button>
                        </div>
                      </td>
                      <td style={styles.td}>{formatMoney(balance?.owed)}</td>
                      <td style={styles.td}>
                        <span style={employee.is_active ? styles.badgeActive : styles.badgeInactive}>
                          {employee.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <input
                          type="text"
                          style={styles.resetPinInput}
                          value={resetPins[employee.id] ?? ''}
                          onChange={e => setResetPins({ ...resetPins, [employee.id]: e.target.value })}
                          placeholder="New PIN"
                          autoComplete="off"
                          inputMode="numeric"
                          data-lpignore="true"
                          data-form-type="other"
                          data-1p-ignore="true"
                        />
                      </td>
                      <td style={styles.td}>
                        <button type="button" style={styles.smallButton} onClick={() => handleResetPin(employee.id)}>
                          Reset PIN
                        </button>
                        <button
                          type="button"
                          style={styles.smallButton}
                          onClick={() => handleToggleEmployeeActive(employee)}
                        >
                          {employee.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          type="button"
                          style={styles.smallButton}
                          onClick={() => {
                            void toggleHistory(employee.id);
                          }}
                        >
                          {selectedEmployeeId === employee.id ? 'Hide Details' : 'View Details'}
                        </button>
                      </td>
                    </tr>

                    {selectedEmployeeId === employee.id && (
                      <tr>
                        <td style={styles.historyCell} colSpan={7}>
                          <div style={styles.historySection}>
                            <div style={styles.balanceSummary}>
                              <div>
                                <div style={styles.balanceLabel}>Hours</div>
                                <div style={styles.balanceValue}>{balance?.total_hours ?? 0}</div>
                              </div>
                              <div>
                                <div style={styles.balanceLabel}>Earned</div>
                                <div style={styles.balanceValue}>{formatMoney(balance?.earned)}</div>
                              </div>
                              <div>
                                <div style={styles.balanceLabel}>Paid</div>
                                <div style={styles.balanceValue}>{formatMoney(balance?.paid)}</div>
                              </div>
                              <div>
                                <div style={styles.balanceLabel}>Owed</div>
                                <div style={styles.balanceValue}>{formatMoney(balance?.owed)}</div>
                              </div>
                            </div>

                            <h4 style={styles.historyTitle}>Log Payment</h4>
                            <div style={styles.addEntryGrid}>
                              <label style={styles.labelInline}>
                                Amount
                                <input
                                  type="number"
                                  min="0.01"
                                  step="0.01"
                                  style={styles.inputInline}
                                  value={paymentDraftByEmployee[employee.id]?.amount ?? ''}
                                  onChange={e =>
                                    setPaymentDraftByEmployee(prev => ({
                                      ...prev,
                                      [employee.id]: {
                                        ...(prev[employee.id] ?? emptyPaymentDraft()),
                                        amount: e.target.value,
                                      },
                                    }))
                                  }
                                />
                              </label>
                              <label style={styles.labelInline}>
                                Paid At
                                <input
                                  type="datetime-local"
                                  style={styles.inputInline}
                                  value={paymentDraftByEmployee[employee.id]?.paidAt ?? ''}
                                  onChange={e =>
                                    setPaymentDraftByEmployee(prev => ({
                                      ...prev,
                                      [employee.id]: {
                                        ...(prev[employee.id] ?? emptyPaymentDraft()),
                                        paidAt: e.target.value,
                                      },
                                    }))
                                  }
                                />
                              </label>
                              <label style={styles.labelInline}>
                                Note
                                <input
                                  type="text"
                                  style={styles.inputInline}
                                  value={paymentDraftByEmployee[employee.id]?.note ?? ''}
                                  onChange={e =>
                                    setPaymentDraftByEmployee(prev => ({
                                      ...prev,
                                      [employee.id]: {
                                        ...(prev[employee.id] ?? emptyPaymentDraft()),
                                        note: e.target.value,
                                      },
                                    }))
                                  }
                                  placeholder="Cash, Venmo, etc."
                                />
                              </label>
                              <button
                                type="button"
                                style={styles.smallButton}
                                onClick={() => {
                                  void handleAddPayment(employee.id);
                                }}
                              >
                                Log Payment
                              </button>
                            </div>

                            <h4 style={styles.historyTitle}>Payment History</h4>
                            {(paymentsByEmployee[employee.id] ?? []).length === 0 ? (
                              <p style={styles.emptyMessage}>No payments logged yet.</p>
                            ) : (
                              <div style={styles.tableWrap}>
                                <table style={styles.table}>
                                  <thead>
                                    <tr>
                                      <th style={styles.th}>Paid At</th>
                                      <th style={styles.th}>Amount</th>
                                      <th style={styles.th}>Note</th>
                                      <th style={styles.th}>Logged By</th>
                                      <th style={styles.th}>Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {(paymentsByEmployee[employee.id] ?? []).map(payment => (
                                      <tr key={payment.id}>
                                        <td style={styles.tdNarrow}>{formatDisplayDate(payment.paid_at)}</td>
                                        <td style={styles.tdNarrow}>{formatMoney(payment.amount)}</td>
                                        <td style={styles.tdNarrow}>{payment.note || '-'}</td>
                                        <td style={styles.tdNarrow}>{payment.created_by}</td>
                                        <td style={styles.tdNarrow}>
                                          <button
                                            type="button"
                                            style={styles.smallButtonDanger}
                                            onClick={() => {
                                              void handleDeletePayment(employee.id, payment.id);
                                            }}
                                          >
                                            Delete
                                          </button>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}

                            <h4 style={styles.historyTitle}>Time Entry History</h4>

                            <div style={styles.filterGrid}>
                              <label style={styles.labelInline}>
                                Start Date
                                <input
                                  type="date"
                                  style={styles.inputInline}
                                  value={historyFilterByEmployee[employee.id]?.startDate ?? ''}
                                  onChange={e =>
                                    setHistoryFilterByEmployee(prev => ({
                                      ...prev,
                                      [employee.id]: {
                                        ...(prev[employee.id] ?? { startDate: '', endDate: '' }),
                                        startDate: e.target.value,
                                      },
                                    }))
                                  }
                                />
                              </label>
                              <label style={styles.labelInline}>
                                End Date
                                <input
                                  type="date"
                                  style={styles.inputInline}
                                  value={historyFilterByEmployee[employee.id]?.endDate ?? ''}
                                  onChange={e =>
                                    setHistoryFilterByEmployee(prev => ({
                                      ...prev,
                                      [employee.id]: {
                                        ...(prev[employee.id] ?? { startDate: '', endDate: '' }),
                                        endDate: e.target.value,
                                      },
                                    }))
                                  }
                                />
                              </label>
                              <button
                                type="button"
                                style={styles.smallButton}
                                onClick={() =>
                                  setHistoryFilterByEmployee(prev => ({
                                    ...prev,
                                    [employee.id]: { startDate: '', endDate: '' },
                                  }))
                                }
                              >
                                Clear Filter
                              </button>
                            </div>

                            <div style={styles.presetButtonRow}>
                              <button
                                type="button"
                                style={styles.smallButton}
                                onClick={() => applyFilterPreset(employee.id, 'thisWeek')}
                              >
                                This Week
                              </button>
                              <button
                                type="button"
                                style={styles.smallButton}
                                onClick={() => applyFilterPreset(employee.id, 'lastWeek')}
                              >
                                Last Week
                              </button>
                              <button
                                type="button"
                                style={styles.smallButton}
                                onClick={() => applyFilterPreset(employee.id, 'thisPayPeriod')}
                              >
                                This Pay Period
                              </button>
                            </div>

                            <div style={styles.filterSummaryText}>
                              {formatActiveRangeLabel(historyFilterByEmployee[employee.id])}
                            </div>

                            <div style={styles.addEntryGrid}>
                              <label style={styles.labelInline}>
                                Clock In
                                <input
                                  type="datetime-local"
                                  style={styles.inputInline}
                                  value={entryDraftByEmployee[employee.id]?.clockIn ?? ''}
                                  onChange={e =>
                                    setEntryDraftByEmployee(prev => ({
                                      ...prev,
                                      [employee.id]: {
                                        ...(prev[employee.id] ?? { clockIn: '', clockOut: '', note: '' }),
                                        clockIn: e.target.value,
                                      },
                                    }))
                                  }
                                />
                              </label>
                              <label style={styles.labelInline}>
                                Clock Out
                                <input
                                  type="datetime-local"
                                  style={styles.inputInline}
                                  value={entryDraftByEmployee[employee.id]?.clockOut ?? ''}
                                  onChange={e =>
                                    setEntryDraftByEmployee(prev => ({
                                      ...prev,
                                      [employee.id]: {
                                        ...(prev[employee.id] ?? { clockIn: '', clockOut: '', note: '' }),
                                        clockOut: e.target.value,
                                      },
                                    }))
                                  }
                                />
                              </label>
                              <label style={styles.labelInline}>
                                Note
                                <input
                                  type="text"
                                  style={styles.inputInline}
                                  value={entryDraftByEmployee[employee.id]?.note ?? ''}
                                  onChange={e =>
                                    setEntryDraftByEmployee(prev => ({
                                      ...prev,
                                      [employee.id]: {
                                        ...(prev[employee.id] ?? { clockIn: '', clockOut: '', note: '' }),
                                        note: e.target.value,
                                      },
                                    }))
                                  }
                                />
                              </label>
                              <button type="button" style={styles.smallButton} onClick={() => { void handleAddEntry(employee.id); }}>
                                Add Entry
                              </button>
                            </div>

                            {historyLoadingByEmployee[employee.id] ? (
                              <p style={styles.emptyMessage}>Loading history...</p>
                            ) : (historyByEmployee[employee.id] ?? []).length === 0 ? (
                              <p style={styles.emptyMessage}>No entries for this employee yet.</p>
                            ) : (
                              <div style={styles.tableWrap}>
                                <table style={styles.table}>
                                  <thead>
                                    <tr>
                                      <th style={styles.th}>Clock In</th>
                                      <th style={styles.th}>Clock Out</th>
                                      <th style={styles.th}>Duration</th>
                                      <th style={styles.th}>Source</th>
                                      <th style={styles.th}>Note</th>
                                      <th style={styles.th}>Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {(historyByEmployee[employee.id] ?? [])
                                      .filter(entry => entryMatchesDateRange(entry, historyFilterByEmployee[employee.id]))
                                      .map(entry => {
                                      const draft = editDraftByEntry[entry.id] ?? {
                                        clockIn: toInputDateTime(entry.clock_in_at),
                                        clockOut: toInputDateTime(entry.clock_out_at),
                                        note: entry.note ?? '',
                                      };

                                      return (
                                        <tr key={entry.id}>
                                          <td style={styles.tdNarrow}>
                                            <input
                                              type="datetime-local"
                                              style={styles.inputInline}
                                              value={draft.clockIn}
                                              onChange={e =>
                                                setEditDraftByEntry(prev => ({
                                                  ...prev,
                                                  [entry.id]: { ...draft, clockIn: e.target.value },
                                                }))
                                              }
                                            />
                                            <div style={styles.metaText}>{formatDisplayDate(entry.clock_in_at)}</div>
                                          </td>
                                          <td style={styles.tdNarrow}>
                                            <input
                                              type="datetime-local"
                                              style={styles.inputInline}
                                              value={draft.clockOut}
                                              onChange={e =>
                                                setEditDraftByEntry(prev => ({
                                                  ...prev,
                                                  [entry.id]: { ...draft, clockOut: e.target.value },
                                                }))
                                              }
                                            />
                                            <div style={styles.metaText}>{formatDisplayDate(entry.clock_out_at)}</div>
                                          </td>
                                          <td style={styles.tdNarrow}>{entry.duration_minutes ?? '-'}</td>
                                          <td style={styles.tdNarrow}>{entry.source}</td>
                                          <td style={styles.tdNarrow}>
                                            <input
                                              type="text"
                                              style={styles.inputInline}
                                              value={draft.note}
                                              onChange={e =>
                                                setEditDraftByEntry(prev => ({
                                                  ...prev,
                                                  [entry.id]: { ...draft, note: e.target.value },
                                                }))
                                              }
                                            />
                                          </td>
                                          <td style={styles.tdNarrow}>
                                            <button
                                              type="button"
                                              style={styles.smallButton}
                                              onClick={() => {
                                                void handleSaveEntry(employee.id, entry.id);
                                              }}
                                            >
                                              Save
                                            </button>
                                            <button
                                              type="button"
                                              style={styles.smallButtonDanger}
                                              onClick={() => {
                                                void handleDeleteEntry(employee.id, entry.id);
                                              }}
                                            >
                                              Delete
                                            </button>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'grid',
    gap: spacing.lg,
    maxWidth: '1000px',
  },
  title: {
    margin: 0,
    fontSize: typographyFlat.fontSize.lg,
    fontWeight: typographyFlat.fontWeight.bold,
    color: colors.text,
  },
  subtitle: {
    margin: 0,
    fontSize: typographyFlat.fontSize.md,
    color: colors.textSecondary,
    marginBottom: spacing.md,
  },
  card: {
    backgroundColor: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    padding: spacing.lg,
  },
  sectionTitle: {
    margin: 0,
    marginBottom: spacing.md,
    fontSize: typographyFlat.fontSize.md,
    fontWeight: typographyFlat.fontWeight.semibold,
    color: colors.text,
  },
  form: {
    display: 'grid',
    gap: spacing.md,
  },
  label: {
    display: 'grid',
    gap: spacing.xs,
    fontSize: typographyFlat.fontSize.sm,
    fontWeight: typographyFlat.fontWeight.medium,
    color: colors.text,
  },
  input: {
    width: '100%',
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    padding: `${spacing.sm} ${spacing.md}`,
    fontSize: typographyFlat.fontSize.md,
    color: colors.text,
  },
  button: {
    backgroundColor: colors.primary,
    color: colors.white,
    border: 'none',
    borderRadius: borderRadius.md,
    padding: `${spacing.sm} ${spacing.md}`,
    fontSize: typographyFlat.fontSize.md,
    fontWeight: typographyFlat.fontWeight.semibold,
    cursor: 'pointer',
  },
  error: {
    backgroundColor: colors.errorLight,
    border: `1px solid ${colors.error}`,
    color: colors.error,
    borderRadius: borderRadius.md,
    padding: spacing.md,
    fontSize: typographyFlat.fontSize.sm,
  },
  emptyMessage: {
    textAlign: 'center',
    color: colors.textSecondary,
    fontSize: typographyFlat.fontSize.md,
    padding: spacing.lg,
  },
  tableWrap: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    textAlign: 'left',
    padding: `${spacing.sm} ${spacing.md}`,
    borderBottom: `1px solid ${colors.border}`,
    backgroundColor: colors.backgroundAlt,
    fontSize: typographyFlat.fontSize.sm,
    fontWeight: typographyFlat.fontWeight.semibold,
    color: colors.textSecondary,
  },
  td: {
    padding: `${spacing.sm} ${spacing.md}`,
    borderBottom: `1px solid ${colors.border}`,
    fontSize: typographyFlat.fontSize.sm,
    color: colors.text,
  },
  tdNarrow: {
    padding: `${spacing.xs} ${spacing.sm}`,
    borderBottom: `1px solid ${colors.border}`,
    fontSize: typographyFlat.fontSize.sm,
    color: colors.text,
    verticalAlign: 'top',
  },
  historyCell: {
    padding: spacing.md,
    borderBottom: `1px solid ${colors.border}`,
    backgroundColor: colors.backgroundAlt,
  },
  historySection: {
    display: 'grid',
    gap: spacing.md,
  },
  historyTitle: {
    margin: 0,
    fontSize: typographyFlat.fontSize.md,
    fontWeight: typographyFlat.fontWeight.semibold,
    color: colors.text,
  },
  addEntryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: spacing.sm,
    alignItems: 'end',
  },
  filterGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: spacing.sm,
    alignItems: 'end',
  },
  presetButtonRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: spacing.xs,
  },
  filterSummaryText: {
    fontSize: typographyFlat.fontSize.xs,
    color: colors.textSecondary,
  },
  labelInline: {
    display: 'grid',
    gap: spacing.xs,
    fontSize: typographyFlat.fontSize.xs,
    color: colors.textSecondary,
  },
  inputInline: {
    width: '100%',
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    padding: `${spacing.xs} ${spacing.sm}`,
    fontSize: typographyFlat.fontSize.sm,
    color: colors.text,
    backgroundColor: colors.surface,
  },
  metaText: {
    marginTop: spacing.xs,
    fontSize: typographyFlat.fontSize.xs,
    color: colors.textSecondary,
  },
  badgeActive: {
    display: 'inline-block',
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
    borderRadius: borderRadius.full,
    padding: `${spacing.xs} ${spacing.sm}`,
    fontSize: typographyFlat.fontSize.xs,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  badgeInactive: {
    display: 'inline-block',
    backgroundColor: '#eceff1',
    color: '#455a64',
    borderRadius: borderRadius.full,
    padding: `${spacing.xs} ${spacing.sm}`,
    fontSize: typographyFlat.fontSize.xs,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  resetPinInput: {
    width: '100px',
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    padding: `${spacing.xs} ${spacing.sm}`,
    fontSize: typographyFlat.fontSize.sm,
    color: colors.text,
  },
  rateInput: {
    width: '90px',
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    padding: `${spacing.xs} ${spacing.sm}`,
    fontSize: typographyFlat.fontSize.sm,
    color: colors.text,
  },
  rateEditRow: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.xs,
  },
  balanceSummary: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
    gap: spacing.sm,
    padding: spacing.sm,
    backgroundColor: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
  },
  balanceLabel: {
    fontSize: typographyFlat.fontSize.xs,
    color: colors.textSecondary,
  },
  balanceValue: {
    fontSize: typographyFlat.fontSize.md,
    fontWeight: typographyFlat.fontWeight.semibold,
    color: colors.text,
  },
  noteInput: {
    width: '180px',
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    padding: `${spacing.xs} ${spacing.sm}`,
    fontSize: typographyFlat.fontSize.sm,
    color: colors.text,
  },
  smallButton: {
    backgroundColor: colors.primary,
    color: colors.white,
    border: 'none',
    borderRadius: borderRadius.md,
    padding: `${spacing.xs} ${spacing.sm}`,
    fontSize: typographyFlat.fontSize.xs,
    fontWeight: typographyFlat.fontWeight.semibold,
    cursor: 'pointer',
    marginRight: spacing.xs,
  },
  smallButtonDanger: {
    backgroundColor: colors.error,
    color: colors.white,
    border: 'none',
    borderRadius: borderRadius.md,
    padding: `${spacing.xs} ${spacing.sm}`,
    fontSize: typographyFlat.fontSize.xs,
    fontWeight: typographyFlat.fontWeight.semibold,
    cursor: 'pointer',
    marginRight: spacing.xs,
  },
};
